const API_URL =
  import.meta.env.VITE_GRAPHQL_URL ||
  `${import.meta.env.VITE_API_URL || 'https://api.sinamonsoft.ar'}/graphql`;

function buildHeaders(includeAuth = true) {
  const headers = {
    'Content-Type': 'application/json',
  };
  const storedTenant = localStorage.getItem('tenant');
  const defaultTenant = import.meta.env.VITE_TENANT || 'sinamon_xx';
  if (storedTenant) {
    try {
      const parsed = JSON.parse(storedTenant);
      headers['X-Tenant'] = parsed?.code || parsed?.domain || defaultTenant;
    } catch (error) {
      headers['X-Tenant'] = storedTenant || defaultTenant;
    }
  } else {
    headers['X-Tenant'] = defaultTenant;
  }
  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
}

async function fetchGraphQL(query, variables = {}, includeAuth = true) {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: buildHeaders(includeAuth),
    body: JSON.stringify({ query, variables }),
  });
  const result = await response.json();
  if (result.errors) {
    throw new Error(result.errors[0]?.message || 'Error GraphQL');
  }
  return result.data;
}

// Fields every label screen needs to show bulto numbering, guía link and print state.
const ETIQUETA_BULTO_FIELDS = `
  id
  codigoEtiqueta
  zpl
  copiaNro
  copiasTotal
  fechaHoraEmision
  servicioDesc
  remitenteNombre
  destinatarioNombre
  totBultos
  totKilos
  totMts3
  estado
  recepcionId
  guiaId
  guiaNumero
  loteId
  bultoNro
  bultosTotal
  impresiones
  dpi
`;

export async function emitirEtiquetas(input) {
  const mutation = `
    mutation EmitirEtiquetas($input: EmitirEtiquetasInput!) {
      emitirEtiquetas(input: $input) {
        ${ETIQUETA_BULTO_FIELDS}
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { input }, true);
  return data.emitirEtiquetas;
}

export async function etiquetasEmitidas(filters = {}, pagination = {}) {
  const query = `
    query EtiquetasEmitidas($filter: EtiquetasFilterInput, $pagination: EtiquetasPaginationInput) {
      etiquetasEmitidas(filter: $filter, pagination: $pagination) {
        items {
          ${ETIQUETA_BULTO_FIELDS}
        }
        pageInfo {
          page
          pageSize
          total
          pages
        }
      }
    }
  `;
  const data = await fetchGraphQL(query, { filter: filters, pagination }, true);
  return data.etiquetasEmitidas;
}

export async function getEtiquetaByNumero(numero, includeAuth = false) {
  const query = `
    query EtiquetaByNumero($numero: String!) {
      getEtiquetaByNumero(numero: $numero) {
        id
        codigoEtiqueta
        fechaHoraEmision
        servicioDesc
        remitenteNombre
        destinatarioNombre
        remitenteDireccion
        destinatarioDireccion
        remitenteCp
        destinatarioCp
        totBultos
        totKilos
        totMts3
        estado
        recepcionId
        guiaId
        items {
          id
          remito
          bultos
          kilos
          mts3
        }
        eventos {
          id
          tipo
          fecha
          observacion
        }
      }
    }
  `;
  const data = await fetchGraphQL(query, { numero }, includeAuth);
  return data.getEtiquetaByNumero;
}

/** Records a print or reprint; the server answers with the labels to queue (same codes). */
export async function registrarImpresionEtiquetas(ids, { dpi, motivo } = {}) {
  const mutation = `
    mutation RegistrarImpresionEtiquetas($ids: [ID!]!, $dpi: Int, $motivo: String) {
      registrarImpresionEtiquetas(ids: $ids, dpi: $dpi, motivo: $motivo) {
        ${ETIQUETA_BULTO_FIELDS}
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { ids, dpi: dpi ?? null, motivo: motivo || null }, true);
  return data.registrarImpresionEtiquetas;
}

export async function escanearEtiqueta(codigo, tipo, observacion) {
  const mutation = `
    mutation EscanearEtiqueta($codigo: String!, $tipo: String!, $observacion: String) {
      escanearEtiqueta(codigo: $codigo, tipo: $tipo, observacion: $observacion) {
        duplicado
        loteEscaneados
        loteTotal
        evento {
          id
          tipo
          fecha
        }
        etiqueta {
          ${ETIQUETA_BULTO_FIELDS}
        }
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { codigo, tipo, observacion: observacion || null }, true);
  return data.escanearEtiqueta;
}

export async function zplTemplate(servicioCodigo) {
  const query = `
    query ZplTemplate($servicioCodigo: Int) {
      zplTemplate(servicioCodigo: $servicioCodigo) {
        id
        tenantKey
        tenantDomain
        servicioCodigo
        nombreTemplate
        zplTemplate
        activo
      }
    }
  `;
  const data = await fetchGraphQL(query, { servicioCodigo }, true);
  return data.zplTemplate;
}

export async function upsertZplTemplate(input) {
  const mutation = `
    mutation UpsertZplTemplate($input: ZplTemplateInput!) {
      upsertZplTemplate(input: $input) {
        id
        tenantKey
        servicioCodigo
        nombreTemplate
        zplTemplate
        activo
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { input }, true);
  return data.upsertZplTemplate;
}

export async function confirmarCargaEtiqueta(numero, observacion, sucursalId) {
  const mutation = `
    mutation ConfirmarCargaEtiqueta($numero: String!, $observacion: String, $sucursalId: ID) {
      confirmarCargaEtiqueta(numero: $numero, observacion: $observacion, sucursalId: $sucursalId) {
        id
        tipo
        fecha
        observacion
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { numero, observacion, sucursalId }, true);
  return data.confirmarCargaEtiqueta;
}

export async function confirmarDescargaEtiqueta(numero, observacion, sucursalId) {
  const mutation = `
    mutation ConfirmarDescargaEtiqueta($numero: String!, $observacion: String, $sucursalId: ID) {
      confirmarDescargaEtiqueta(numero: $numero, observacion: $observacion, sucursalId: $sucursalId) {
        id
        tipo
        fecha
        observacion
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { numero, observacion, sucursalId }, true);
  return data.confirmarDescargaEtiqueta;
}

export async function confirmarRecepcionEtiqueta(numero, observacion, sucursalId) {
  const mutation = `
    mutation ConfirmarRecepcionEtiqueta($numero: String!, $observacion: String, $sucursalId: ID) {
      confirmarRecepcionEtiqueta(numero: $numero, observacion: $observacion, sucursalId: $sucursalId) {
        id
        tipo
        fecha
        observacion
      }
    }
  `;
  const data = await fetchGraphQL(mutation, { numero, observacion, sucursalId }, true);
  return data.confirmarRecepcionEtiqueta;
}
